<script lang="ts">
  import { community, communitySlug } from "app/stores/community";
  import KBNavigation from "./KBNavigation.svelte";
  import { get } from "svelte/store";
  import { canAddDocument } from "app/stores/permissions";
  import Dropdown from "$components/+blocks/Dropdown/Dropdown.svelte";
  import IconButtonTrigger from "$components/+blocks/Dropdown/fields/IconButtonTrigger.svelte";
  import Action from "$components/+blocks/Dropdown/fields/Action.svelte";
  import { alerts } from "app/stores/alert";
  import { docURL } from "app/utils/urls";
  import { goto } from "$app/navigation";
  import PromptDialogue from "$components/+blocks/Dialogue/PromptDialogue.svelte";
  import { type DocumentsStore, DragType, isDraggingAtTheRoot } from "app/stores/documents";
  import { Plus, Category, Doc, Section } from "$components/+blocks/OVIcon";
  import KBDrafts from "$components/knowledge-base/KnowledgeBaseNavigation/KBDrafts.svelte";
  import KBFolder from "$components/knowledge-base/KnowledgeBaseNavigation/KBFolder.svelte";
  import type { WritableFolder } from "models/documents";
  import { dndzone, TRIGGERS, type DndEvent } from "svelte-dnd-action";
  import { flip } from "svelte/animate";
  import { getMediaQuery } from "$utils/mediaQuery";
  import { CategoryTypeEnum } from "@outverse-video/outverse-api-client-lib";
  import {
    handleSharedDraggingBehaviour,
    isDndPlaceholder,
    transformDraggedElement,
  } from "./dndUtils";
  import DndPlaceholder from "$components/knowledge-base/KnowledgeBaseNavigation/DndPlaceholder.svelte";
  import CategoryModal from "./CategoryModal.svelte";
  import { onMount } from "svelte";
  import { debounce } from "app/utils";
  import { fadeSlide } from "app/transitions";
  import { getStores } from "app/stores";
  import { fade } from "svelte/transition";
  import SkipLink from "$components/+blocks/SkipLink/SkipLink.svelte";
  import { CanceledError } from "axios";

  const ANIMATION_DURATION_MS = 110;
  const FLIP_DURATION_MS = 200;

  export let docs: DocumentsStore;

  // we store and pass down to the category item whether the drag is happening within a section or not
  let openCreateCategoryModal = false;
  let openCreateSectionModal = false;
  let categoryType: CategoryTypeEnum = CategoryTypeEnum.Category;

  const isDesktop = getMediaQuery("desktop");
  const { userPreferences } = getStores();
  const { topLevelFolders, draftDocuments, currentDragType } = docs;

  async function sortFolders(e: CustomEvent<DndEvent<WritableFolder>>) {
    docs.preventUpdates(); // prevent server updates from colliding with user

    handleSharedDraggingBehaviour(e);

    // if the user drags an item, we set the is draggin at the root value to true
    if (e.detail.info.trigger === TRIGGERS.DRAG_STARTED) {
      $isDraggingAtTheRoot = true;
    }

    // user is moving doc - update UI
    if (e.detail.info.trigger === TRIGGERS.DRAG_STARTED) {
      const destination = $topLevelFolders.findIndex((folder) => folder.uuid === e.detail.info.id);
      const movingFolder = $topLevelFolders[destination];
      $currentDragType =
        get(movingFolder).categoryType === CategoryTypeEnum.Section
          ? DragType.Section
          : DragType.NestedFolder;
    }

    $topLevelFolders = e.detail.items;

    // doc dropped into a zone, update the order in the backend
    if (e.detail.info.trigger === TRIGGERS.DROPPED_INTO_ZONE) {
      const destination = $topLevelFolders.findIndex((folder) => folder.uuid === e.detail.info.id);
      $topLevelFolders[destination].moveAfterDragAndDrop(destination);
    }

    // dropped outside of any zone, we don't need to update the order
  }

  let element: HTMLElement | null = null;
  let isSectionOverflowing: boolean;
  let observer: ResizeObserver;

  const handleBottomFade = debounce(() => {
    if (!element) return;

    // if user has scrolled to the bottom, we don't need to show the fade
    if (Math.ceil(element?.scrollTop) >= element?.scrollHeight - element.offsetHeight) {
      isSectionOverflowing = false;
      return;
    }
    // if section is expanded, we only need to show the fade if the section is overflowing
    isSectionOverflowing = element.scrollHeight > element.offsetHeight;
  }, ANIMATION_DURATION_MS / 2);

  onMount(() => {
    observer = new ResizeObserver(handleBottomFade);
    if (element) {
      observer.observe(element);
      element.addEventListener("scroll", handleBottomFade);
      return () => {
        observer.disconnect();
        element?.removeEventListener("scroll", handleBottomFade);
      };
    }
  });
</script>

<div class="relative flex flex-col overflow-hidden">
  <SkipLink label="Skip navigation" />
  {#if $canAddDocument}
    <button
      type="button"
      in:fadeSlide
      on:click={() => !$isDesktop && userPreferences.toggleLeftPanelExpansion()}
      class="group mx-1.5 flex h-[32px] items-center justify-center p-2 pr-1.5 text-xs font-semibold text-black/80 dark:text-white/90"
    >
      <span class="mr-auto min-w-0 truncate">Create new</span>
      <Dropdown>
        <IconButtonTrigger
          slot="trigger"
          let:active
          {active}
          variant="tertiary"
          icon={Plus}
          a11yLabel="Add a document, section, or category"
        />

        <Action
          title="New doc"
          icon={Doc}
          on:click={async () => {
            try {
              const doc = await docs.createDocument();
              alerts.success("New document created.");
              goto(
                docURL({
                  communitySlug: $communitySlug,
                  docUuid: doc.uuid,
                  docSlug: "untitled-document",
                })
              );
            } catch (error) {
              if (error instanceof CanceledError) {
                alerts.error("Failed to create document.");
              } else {
                throw error;
              }
            }
          }}
        />
        <Action
          title="New category"
          icon={Category}
          on:click={() => {
            categoryType = CategoryTypeEnum.Category;
            openCreateCategoryModal = true;
          }}
        />
        <Action
          title="New section"
          icon={Section}
          on:click={() => {
            categoryType = CategoryTypeEnum.Section;
            openCreateSectionModal = true;
          }}
        />
      </Dropdown>
    </button>
  {/if}

  <nav
    class="mb-1 scrollbar-thin overflow-x-hidden overflow-y-auto overscroll-contain px-1.5 scrollbar-stable md:scrollbar-invisible md:hover:scrollbar-visible"
    bind:this={element}
  >
    <KBNavigation {docs} />

    <!-- Categorised docs -->
    <div
      class="rounded"
      use:dndzone={{
        items: $topLevelFolders,
        flipDurationMs: FLIP_DURATION_MS,
        dragDisabled: !$canAddDocument || !$isDesktop,
        type: "docs",
        dropTargetStyle: {},
        dropTargetClasses: [
          "min-h-[24px]",
          "border",
          "border-dashed",
          "border-blue-400/75",
          "dark:border-blue-400/45",
        ],
        // accepts sections or categories
        dropFromOthersDisabled: $currentDragType === DragType.NestedDocument,
        transformDraggedElement,
      }}
      on:consider={sortFolders}
      on:finalize={sortFolders}
    >
      {#if $topLevelFolders.length === 0}
        <!--
      Important, when these nav dropzones are completely empty, there is a bug where dropzones don't open properly. It is
      not enough to set min height in dropTargetClasses.
      -->
        <div />
      {/if}

      {#each $topLevelFolders as folder (folder.uuid)}
        <div animate:flip={{ duration: FLIP_DURATION_MS }}>
          {#if isDndPlaceholder(folder)}
            <DndPlaceholder />
          {:else}
            <div>
              <KBFolder {docs} {folder} hasDraggedEnteredParent={$isDraggingAtTheRoot} />
            </div>
          {/if}
        </div>
      {/each}
    </div>
    {#if $canAddDocument}
      <KBDrafts drafts={$draftDocuments} />
    {/if}

    <CategoryModal
      bind:open={openCreateCategoryModal}
      title="Create category"
      confirmLabel="Create"
      blankErrorMessage="Please enter a name for the new category"
      on:submit={({ detail: { name, icon, iconDark } }) => {
        docs.createFolder(name, categoryType, icon, iconDark);
        openCreateCategoryModal = false;
      }}
    />

    <PromptDialogue
      bind:open={openCreateSectionModal}
      title="Create section"
      confirmLabel="Create"
      blankErrorMessage="Please enter a name for the new section"
      on:submit={(ev) => {
        docs.createFolder(ev.detail.value, categoryType);
        openCreateSectionModal = false;
      }}
    />
  </nav>

  <!-- gradient used to fade out the expanded section nav items -->
  {#if isSectionOverflowing}
    <div
      class="pointer-events-none absolute bottom-0 z-0 hidden h-[42px] w-full bg-gradient-to-t from-white to-transparent lg:block dark:to-transparent"
      class:dark:from-dark-custom-background={$community.backgroundColorDark}
      class:dark:from-neutral-900={!$community.backgroundColorDark}
      transition:fade={{ duration: ANIMATION_DURATION_MS }}
    />
  {/if}
</div>
